import { Col, Row, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useEffect } from 'react';
import { Button } from 'src/components/Button/Button';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { Empty } from 'src/components/Empty/Empty';
import { ListPage } from 'src/components/FormPages/ListPage';
import { LoanApplicationStatusView } from 'src/components/LoanApplicationStatusView/LoanApplicationStatusView';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { LoanApplicationDto } from 'src/generated-api-client';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { downloadFileBinary } from 'src/utils/FileHelper';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';

export const LoanApplicationsPage = observer(() => {
    useEffect(() => {
        return () => {
            LoanApplicationsStore.exportActiveApplicationsLoader.reset();
        };
    }, []);

    return (
        <ListPage
            pageName="LoanApplications"
            store={LoanApplicationsStore}
            header={
                <Row gutter={16} align="middle">
                    <Col flex={1}>
                        <Typography.Title level={3}>
                            {t('App.Pages.LoanApplications.Title')}
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Button
                            onClick={async () => {
                                const file =
                                    await LoanApplicationsStore.exportActiveApplications();

                                const fileName = `active_loan_applications_${moment().format(
                                    'yyyyMMDD_hhmmss',
                                )}`;

                                file && downloadFileBinary(file, fileName);
                            }}
                            loading={
                                LoanApplicationsStore
                                    .exportActiveApplicationsLoader.isLoading
                            }
                        >
                            {t(
                                'App.Components.TransactionFilterPanel.DownloadActiveApplications',
                            )}
                        </Button>
                    </Col>
                </Row>
            }
            empty={
                <Empty description={t('App.Pages.LoanApplications.Empty')} />
            }
            renderItem={(item: Entity) => {
                const application = item as unknown as LoanApplicationDto;

                return (
                    <Card
                        key={application.id}
                        onClick={() => {
                            const path =
                                application.id !== undefined
                                    ? EntityLinks.loanApplications.itemDetails?.(
                                          application.id,
                                      )
                                    : undefined;

                            if (path) {
                                Router.navigate(path);
                            }
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={5}>
                                <DataView
                                    label={t(
                                        'App.Pages.LoanApplications.Number',
                                    )}
                                    value={application.number}
                                />
                            </Col>
                            <Col xs={24} sm={5}>
                                <DataView
                                    label={t(
                                        'App.Pages.LoanApplications.Merchant.Name',
                                    )}
                                    value={application.merchant?.name}
                                />
                            </Col>
                            <Col xs={24} sm={5}>
                                <DataView
                                    label={t(
                                        'App.Pages.LoanApplications.CreatedAt',
                                    )}
                                    value={application.createdAt}
                                    formatValue="dateTimeSeconds"
                                />
                            </Col>
                            <Col xs={24} sm={5}>
                                <DataView
                                    label={t(
                                        'App.Pages.LoanApplications.Status',
                                    )}
                                    value={
                                        application.status ? (
                                            <LoanApplicationStatusView
                                                status={application.status}
                                            />
                                        ) : undefined
                                    }
                                />
                            </Col>
                            <Col xs={24} sm={4}>
                                <DataView
                                    label={t(
                                        'App.Pages.LoanApplications.Product.MaxAmount',
                                    )}
                                    value={
                                        application.finalOffer?.financingAmount
                                    }
                                    formatValue="currency-sar"
                                />
                            </Col>
                        </Row>
                    </Card>
                );
            }}
        />
    );
});
